import { useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import { Typography } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { PromptTemplate } from '@viq-admin/types';
import {
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';
import { PIPELINE_LABELS } from '../../api/promptStore';
import { AppliedCompaniesModal } from './AppliedCompaniesModal';
import { PromptReference } from '../prompt-guide/PromptReference';

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — see DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
};

/**
 * List of reusable prompt append templates (global, cross-tenant — mirrors
 * `ToolkitsList`'s shape, simplified: no secrets, no per-type field groups).
 * Opening a template is the ROW CLICK (→ `/prompt-templates/:id`,
 * `PromptTemplateDetail` — a read-first view with an in-place Edit toggle);
 * the kebab's sole action opens `AppliedCompaniesModal`, which lists the
 * applied companies (with detach / reapply controls) and lets the operator
 * apply the template to more companies.
 *
 * `read PromptTemplate` gates the page (route-level, App.tsx); the modal's
 * mutating actions are gated on `manage PromptTemplate`. "View companies"
 * itself is ungated — it's a read action, and the page-level gate already
 * covers it.
 */
export function PromptTemplatesList() {
  const { tableProps } = useTable<PromptTemplate>({
    resource: 'prompt-templates',
    // DataTable owns paging; Refine must not slice the payload.
    pagination: { mode: 'off' },
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const columns: DataColumn<PromptTemplate>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 240 },
    {
      dataIndex: 'pipeline',
      key: 'pipeline',
      title: 'Pipeline',
      kind: 'enum',
      width: 150,
      projectValue: (r) => PIPELINE_LABELS[r.pipeline],
      render: (_: unknown, r: PromptTemplate) => PIPELINE_LABELS[r.pipeline],
    },
    {
      dataIndex: 'appliedCompanyCount',
      key: 'appliedCompanyCount',
      title: 'Applied to',
      kind: 'number',
      width: 120,
    },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: PromptTemplate) => (
        <RowActionsMenu
          actions={[
            {
              key: 'view-companies',
              label: 'View companies',
              onRun: () => setSelectedId(record.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">
        Templates are the only way to give a company custom rules. Applying one replaces any rules
        that company already has. <Link to="/prompt-guide">How this works</Link>
      </Typography.Paragraph>
      <PromptReference sections={['assembly', 'output']} />
      <List>
        <DataTable<PromptTemplate>
          tableProps={tableProps}
          columns={columns}
          rowKey="id"
          storageKey="promptTemplatesList"
          state={state}
          onStateChange={setState}
          defaultState={DEFAULT_STATE}
          searchFields={['name']}
          searchPlaceholder="Search prompt templates"
          onRowClick={(record) => navigate(`/prompt-templates/${record.id}`)}
          empty={{
            title: 'No prompt templates yet',
            description: 'Create the first template to get started.',
          }}
        />
      </List>
      {selectedId && (
        <AppliedCompaniesModal
          templateId={selectedId}
          open
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
}
